import { useCallback, useState } from "react";
import {
  EmailAuthProvider,
  GoogleAuthProvider,
  OAuthProvider,
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  RecaptchaVerifier,
  getMultiFactorResolver,
  reauthenticateWithCredential,
  reauthenticateWithPopup,
} from "firebase/auth";
import { getAuth } from "firebase/auth";
import { facebookProvider } from "../firebase";

export function getProviderType(user) {
  if (!user?.providerData?.length) return "password";
  const providers = user.providerData.map((p) => p.providerId);
  if (providers.includes("google.com")) return "google.com";
  if (providers.includes("apple.com")) return "apple.com";
  if (providers.includes("facebook.com")) return "facebook.com";
  if (providers.includes("microsoft.com")) return "microsoft.com";
  return "password";
}

export function getProviderLabel(providerType) {
  switch (providerType) {
    case "google.com":
      return "Google";
    case "apple.com":
      return "Apple";
    case "facebook.com":
      return "Facebook";
    case "microsoft.com":
      return "Microsoft";
    default:
      return "your password";
  }
}

function getRecaptchaVerifier(authInstance, recaptchaContainerRef, existingRef) {
  if (existingRef?.current) {
    existingRef.current.clear();
    existingRef.current = null;
  }
  if (recaptchaContainerRef?.current) {
    recaptchaContainerRef.current.innerHTML = "";
  }
  const verifier = new RecaptchaVerifier(authInstance, recaptchaContainerRef.current, {
    size: "invisible",
  });
  if (existingRef) existingRef.current = verifier;
  return verifier;
}

export async function reauthenticateUser(user, { password, authInstance = getAuth() } = {}) {
  const providerType = getProviderType(user);

  if (providerType === "password") {
    const credential = EmailAuthProvider.credential(user.email, password);
    await reauthenticateWithCredential(user, credential);
    return;
  }

  if (providerType === "google.com") {
    await reauthenticateWithPopup(user, new GoogleAuthProvider());
    return;
  }

  if (providerType === "apple.com") {
    await reauthenticateWithPopup(user, new OAuthProvider("apple.com"));
    return;
  }

  if (providerType === "facebook.com") {
    await reauthenticateWithPopup(user, facebookProvider);
    return;
  }

  if (providerType === "microsoft.com") {
    const provider = new OAuthProvider("microsoft.com");
    provider.setCustomParameters({ tenant: "consumers" });
    await reauthenticateWithPopup(user, provider);
    return;
  }

  throw new Error("Unsupported sign-in provider for re-authentication.");
}

export async function startMfaReauthChallenge(
  err,
  { authInstance = getAuth(), recaptchaContainerRef, recaptchaVerifierRef } = {}
) {
  const resolver = getMultiFactorResolver(authInstance, err);
  const phoneHint = resolver.hints.find(
    (h) => h.factorId === PhoneMultiFactorGenerator.FACTOR_ID
  );
  if (!phoneHint) {
    throw new Error("No phone-based second factor found.");
  }
  const recaptchaVerifier = getRecaptchaVerifier(
    authInstance,
    recaptchaContainerRef,
    recaptchaVerifierRef
  );
  const phoneAuthProvider = new PhoneAuthProvider(authInstance);
  const verificationId = await phoneAuthProvider.verifyPhoneNumber(
    { multiFactorHint: phoneHint, session: resolver.session },
    recaptchaVerifier
  );
  return { resolver, verificationId };
}

export async function completeMfaReauth(resolver, verificationId, code) {
  const credential = PhoneAuthProvider.credential(verificationId, code);
  const assertion = PhoneMultiFactorGenerator.assertion(credential);
  await resolver.resolveSignIn(assertion);
}

export function useReauthentication({ recaptchaContainerRef, recaptchaVerifierRef } = {}) {
  const authInstance = getAuth();
  const user = authInstance.currentUser;

  const [reAuthOpen, setReAuthOpen] = useState(false);
  const [reAuthPassword, setReAuthPassword] = useState("");
  const [reAuthError, setReAuthError] = useState("");
  const [reAuthLoading, setReAuthLoading] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [reAuthMfaResolver, setReAuthMfaResolver] = useState(null);
  const [reAuthMfaVerificationId, setReAuthMfaVerificationId] = useState(null);
  const [reAuthMfaCode, setReAuthMfaCode] = useState("");
  const [reAuthMfaSmsSending, setReAuthMfaSmsSending] = useState(false);

  const resetReAuthState = useCallback(() => {
    setReAuthOpen(false);
    setReAuthPassword("");
    setReAuthError("");
    setReAuthMfaResolver(null);
    setReAuthMfaVerificationId(null);
    setReAuthMfaCode("");
    setReAuthMfaSmsSending(false);
    setPendingAction(null);
  }, []);

  const handleReAuthMfaChallenge = useCallback(
    async (err) => {
      setReAuthMfaSmsSending(true);
      try {
        const { resolver, verificationId } = await startMfaReauthChallenge(err, {
          authInstance,
          recaptchaContainerRef,
          recaptchaVerifierRef,
        });
        setReAuthMfaResolver(resolver);
        setReAuthMfaVerificationId(verificationId);
      } finally {
        setReAuthMfaSmsSending(false);
      }
    },
    [authInstance, recaptchaContainerRef, recaptchaVerifierRef]
  );

  const handleReAuthConfirm = useCallback(
    async (actionOverride, providerOverride, onSuccess) => {
      const action = actionOverride || pendingAction;
      setReAuthLoading(true);
      setReAuthError("");
      try {
        const providerType = providerOverride || getProviderType(user);
        if (providerType === "password") {
          await reauthenticateUser(user, { password: reAuthPassword, authInstance });
        } else {
          await reauthenticateUser(user, { authInstance });
        }
        setReAuthOpen(false);
        if (onSuccess) {
          await onSuccess(action);
        }
        return true;
      } catch (err) {
        if (err.code === "auth/multi-factor-auth-required") {
          try {
            await handleReAuthMfaChallenge(err);
          } catch (mfaErr) {
            setReAuthError(mfaErr.message || "Failed to send verification code.");
          } finally {
            setReAuthLoading(false);
          }
          return false;
        }
        const msg =
          err.code === "auth/wrong-password" || err.code === "auth/invalid-credential"
            ? "Incorrect password. Please try again."
            : err.code === "auth/popup-closed-by-user"
              ? "Sign-in popup was closed. Please try again."
              : err.message || "Re-authentication failed.";
        if (reAuthOpen) {
          setReAuthError(msg);
        }
        throw err;
      } finally {
        setReAuthLoading(false);
      }
    },
    [
      authInstance,
      handleReAuthMfaChallenge,
      pendingAction,
      reAuthOpen,
      reAuthPassword,
      user,
    ]
  );

  const handleReAuthMfaVerify = useCallback(
    async (onSuccess) => {
      if (!reAuthMfaResolver || !reAuthMfaVerificationId || reAuthMfaCode.length !== 6) return false;
      setReAuthLoading(true);
      setReAuthError("");
      try {
        await completeMfaReauth(reAuthMfaResolver, reAuthMfaVerificationId, reAuthMfaCode);
        setReAuthOpen(false);
        setReAuthMfaResolver(null);
        setReAuthMfaVerificationId(null);
        setReAuthMfaCode("");
        const action = pendingAction;
        if (onSuccess) {
          await onSuccess(action);
        }
        return true;
      } catch (err) {
        if (err.code === "auth/invalid-verification-code") {
          setReAuthError("Invalid code. Check your SMS and try again.");
        } else {
          setReAuthError(err.message || "Verification failed.");
        }
        return false;
      } finally {
        setReAuthLoading(false);
      }
    },
    [pendingAction, reAuthMfaCode, reAuthMfaResolver, reAuthMfaVerificationId]
  );

  const requireReAuth = useCallback(
    (action, onSocialReauth) => {
      setReAuthError("");
      const providerType = getProviderType(user);
      setPendingAction(action);

      if (providerType === "password") {
        setReAuthOpen(true);
        setReAuthPassword("");
        setReAuthError("");
      } else if (onSocialReauth) {
        onSocialReauth(action, providerType);
      }
    },
    [user]
  );

  return {
    user,
    reAuthOpen,
    setReAuthOpen,
    reAuthPassword,
    setReAuthPassword,
    reAuthError,
    setReAuthError,
    reAuthLoading,
    reAuthMfaResolver,
    reAuthMfaVerificationId,
    reAuthMfaCode,
    setReAuthMfaCode,
    reAuthMfaSmsSending,
    pendingAction,
    resetReAuthState,
    requireReAuth,
    handleReAuthConfirm,
    handleReAuthMfaVerify,
    getProviderType: () => getProviderType(user),
    getProviderLabel,
  };
}
